import { Component, ChangeDetectorRef, inject, OnDestroy } from '@angular/core';
import { EventBillingReportFormArray, EventBillingReportProps, EventBillingReportTableSchema, EventBillingSearchConfig } from '../../model/event-billing-report.model';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, FETCH_METHODS, SearchConfig } from '../../model/api.model';
import { CommonUtils } from '../../utils/common.utils';
import { DynamicFormArray, Props } from '../../model/form.model';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { BaseComponent } from '../base/base.component';
import { CommonService } from '../../services/common.service';
import { Subject } from 'rxjs';
import { FieldNames } from '../../model/form-fields.model';
type EventData = { eventId: number; eventName: string; year: string };

const EventBillingReportFetchConfig = {
  getEventsAndYears: {
    path: 'getEventsAndYears',
    method: FETCH_METHODS['Get'],
    urlPath: ApiEnvNames['EventBilling'],
  },
  getDistinctOrganisations: {
    path: 'getDistinctOrganisations',
    method: FETCH_METHODS['Get'],
    urlPath: ApiEnvNames['EventBilling'],
  },
} as const;

@Component({
  selector: 'app-event-billing-report',
  standalone: true,
  imports: [PageContainerComponent],
  templateUrl: './event-billing-report.component.html'
})
export class EventBillingReportComponent extends BaseComponent implements OnDestroy {
  protected data = 'EventBillingReportComponent';
  public dataTableSchema: DataTableSchema = structuredClone(EventBillingReportTableSchema);
  public filterSchema: DynamicFormArray = structuredClone(EventBillingReportFormArray);
  protected props: Props = EventBillingReportProps;
  title = "";
  service = inject(CommonService);
  commonUtils = inject(CommonUtils);
  changeRef = inject(ChangeDetectorRef);
  readonly destroy$ = new Subject<void>();

  override ngOnInit() {
    super.ngOnInit();
    super.commonFunction();
    this.setTableLoadingState(true);
    this.loadOptions();
  }

  private async loadOptions() {
    let eventData: EventData[] = [];
    try {
      const res = await this.service.getDataFromDataSource(EventBillingReportFetchConfig.getEventsAndYears, ApiEnvNames['EventBilling']);
      if (res?.status === API_STATUS.Success) {
        const raw = res.result;
        eventData = Array.isArray(raw) ? raw : ((raw as any[])?.[0]?.result ?? []);
      }
    } catch (err) { console.info('loadOptions error:', err); }

    const uniqueYears = [...new Set(eventData.map(e => String(e.year)))];
    const yearField = this.filterSchema.FieldsArray.find(f => f['fieldName'] === FieldNames['Year']);
    const eventField = this.filterSchema.FieldsArray.find(f => f['fieldName'] === FieldNames['Event']);
    const dealerField = this.filterSchema.FieldsArray.find(f => f['fieldName'] === FieldNames['EventDealer']);

    if (yearField) {
      yearField['options'] = uniqueYears.map(y => ({ fieldLabel: y, fieldValue: y }));
      yearField['isLoading'] = false;
      yearField['disabled'] = false;
    }
    if (eventField) {
      const filtered = yearField?.['selectedValue']
        ? eventData.filter(e => String(e.year) === String(yearField['selectedValue']))
        : eventData;
      eventField['options'] = filtered.map(e => ({ fieldLabel: e.eventName, fieldValue: e.eventName }));
      eventField['isLoading'] = false;
      if (filtered.length > 0) {
        eventField['defaultValue'] = filtered[0].eventName;
        eventField['selectedValue'] = filtered[0].eventName;
      }
    }

    if (dealerField) {
      try {
        const dealerRes = await this.service.getDataFromDataSource(EventBillingReportFetchConfig.getDistinctOrganisations, ApiEnvNames['EventBilling']);
        dealerField['options'] = dealerRes?.status === API_STATUS.Success && Array.isArray(dealerRes.result)
          ? dealerRes.result.map(i => ({ fieldLabel: i['organisation'], fieldValue: i['organisation'] }))
          : [];
      } catch (err) {
        console.info('loadDealerOptions error:', err);
        dealerField['options'] = [];
      }
      dealerField['isLoading'] = false;
    }

    this.checkForAnyEmptyFields();
    this.onSearchClicked('');
  }

  override onSearchClicked = (searchParam: any) => {
    if (searchParam) {
      this.dataTableSchema.dataSource = [];
    }
    this.fetchAllData(this.getSearchConfig());
  };

  override onResetClicked = () => {
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map(f => ({ ...f, selectedValue: f['defaultValue'] }));
    this.dataTableSchema = { ...this.dataTableSchema, inputFilter: '', dataSource: [] };
    const eventField = this.filterSchema.FieldsArray.find(f => f['fieldName'] === FieldNames['Event']);
    this.checkForAnyEmptyFields();
    if(eventField?.['selectedValue']){
    this.onSearchClicked('');
    }
  };

  getSearchConfig = (): any => {
    const searchConfig = structuredClone(EventBillingSearchConfig);
    this.filterSchema['FieldsArray'].forEach(field => {
      if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
        const bodyKey = field['fieldName'] === FieldNames['EventDealer'] ? FieldNames['Dealer'] : field['fieldName'];
        searchConfig['body'][bodyKey] = field['selectedValue'];
      }
    });
    if (this.dataTableSchema.inputFilter.length > 0) {
      searchConfig['body']['searchText'] = this.dataTableSchema.inputFilter;
    }
    if (this.sortBy && Object.keys(this.sortBy).length > 0) {
      if (!searchConfig['body']) {
        searchConfig['body'] = {};
      }
      searchConfig['body']['sortBy'] = this.sortBy;
    }
    return searchConfig;
  };

  fetchAllData = (searchParam: SearchConfig) => {
    searchParam['body'] ??= {};
    this.dataTableSchema.dataSource = [];
    this.setTableLoadingState(true);
    this.filterSchema.FieldsArray.forEach(field => {
      const key = field['fieldName'] === FieldNames['EventDealer'] ? FieldNames['Dealer'] : field['fieldName'];
      searchParam['body'][key] = field['selectedValue'];
    });
    this.filterSchema['payload'] = searchParam['body'];
    this.service.getDataFromDataSource(searchParam, ApiEnvNames['EventBilling'])
      .then(res => {
        if (res['status'] === API_STATUS.Success && Array.isArray(res.result)) {
          this.dataTableSchema.dataSource = res.result;
      } else {
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
      }
        this.setTableLoadingState(false);
      })
      .catch(err => { console.info(err); this.setTableLoadingState(false); });
  };

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
